import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { AxiosError, HttpStatusCode } from 'axios';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { z } from 'zod';
import { QueryKey } from '@/Config/api/queryKey';
import i18n from '@/Config/app/i18n';
import { ActionsApi } from '@/Domain/api/Action/ApiAction/Actions.api';
import { HttpActionDto } from '@/Domain/api/Action/dto/Action.dto';
import { ErrorResponse } from '@/Domain/api/App/dto/Error.dto';
import { HubSpotTemplate } from '@/Domain/components/Action/HubSpotTemplates/hubspotTemplates.const';
import { HttpMethodEnum } from '@/Domain/enum/HttpMethod.enum';
import { ParameterTypeEnum } from '@/Domain/enum/ParameterType.enum';
import { PostHogEventEnum } from '@/Domain/enum/PostHogEvent.enum';
import { ActionHelper } from '@/Domain/helper/Action.helper';
import { HttpActionHelper } from '@/Domain/helper/HttpAction.helper';
import { usePostHogAnalytics } from '@/Domain/hooks/App/usePostHog/usePostHogAnalytics.hook';
import { MODAL_NAMES, useModalStore } from '@/Domain/store/modal.store';

// Centralized limits configuration
export const ACTION_LIMITS = {
  NAME: 64,
  DESCRIPTION: 1000,
  URL: 10000,
  BODY_KEY: 100,
  BODY_VALUE: 1000, // actually infinite in database
  QUERY_KEY: 100,
  QUERY_VALUE: 300,
  HEADER_KEY: 100,
  HEADER_VALUE: 1000,
  PARAMETER_KEY: 100,
  PARAMETER_DESCRIPTION: 500,
  PARAMETER_DEFAULT_VALUE: 1000,
  PARAMETER_COUNT: 30,
  THINKING_MESSAGE: 1000,
  RESPONSE_FILTER_PATH: 1000,
  RESPONSE_FILTER_COUNT: 50,
  MEMORY_CAPTURE_PATH: 1000,
  MEMORY_CAPTURE_KEY: 100,
  MEMORY_CAPTURE_COUNT: 50,
} as const;

export const PARAMETER_LIMIT = ACTION_LIMITS.PARAMETER_COUNT;

const buildMetadataCaptureSchema = () =>
  z
    .array(
      z.object({
        id: z.number().or(z.string()),
        json_path: z
          .string()
          .min(1, { message: i18n.t('actions_page:sheet.form.validation.jsonpath_required') })
          .max(ACTION_LIMITS.MEMORY_CAPTURE_PATH, {
            message: i18n.t('actions_page:sheet.form.validation.jsonpath_too_long', {
              limit: ACTION_LIMITS.MEMORY_CAPTURE_PATH,
            }),
          }),
        metadata_key: z
          .string()
          .min(1, { message: i18n.t('actions_page:sheet.form.validation.memory_key_required') })
          .max(ACTION_LIMITS.MEMORY_CAPTURE_KEY, {
            message: i18n.t('actions_page:sheet.form.validation.memory_key_too_long', {
              limit: ACTION_LIMITS.MEMORY_CAPTURE_KEY,
            }),
          })
          .regex(/^[a-zA-Z][a-zA-Z0-9_]*$/, {
            message: i18n.t('actions_page:sheet.form.validation.memory_key_invalid'),
          }),
      }),
    )
    .max(ACTION_LIMITS.MEMORY_CAPTURE_COUNT, {
      message: i18n.t('actions_page:sheet.form.validation.memory_capture_too_many', {
        limit: ACTION_LIMITS.MEMORY_CAPTURE_COUNT,
      }),
    })
    .superRefine((items, ctx) => {
      const seen = new Set<string>();
      items.forEach((item, index) => {
        const key = item.metadata_key.trim();
        if (key && seen.has(key)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: [index, 'metadata_key'],
            message: i18n.t('actions_page:sheet.form.validation.memory_key_duplicate'),
          });
        }
        seen.add(key);
      });
    })
    .default([]);

const actionSchema = (isActive: boolean) => {
  if (!isActive) {
    return z.object({
      name: z.string().max(ACTION_LIMITS.NAME, {
        message: i18n.t('actions_page:sheet.form.name.limit_reached', {
          limit: ACTION_LIMITS.NAME,
        }),
      }),
      thinking_message: z.string().max(ACTION_LIMITS.THINKING_MESSAGE, {
        message: i18n.t('actions_page:sheet.form.thinking_message.limit_reached', {
          limit: ACTION_LIMITS.THINKING_MESSAGE,
        }),
      }),
      description: z.string().max(ACTION_LIMITS.DESCRIPTION, {
        message: i18n.t('actions_page:sheet.form.validation.description_error_too_long', {
          limit: ACTION_LIMITS.DESCRIPTION,
        }),
      }),
      url: z.preprocess(
        (val) => (typeof val === 'string' && val.trim() === '' ? undefined : val),
        z
          .string()
          .max(ACTION_LIMITS.URL, {
            message: i18n.t('actions_page:sheet.form.validation.action_url_error_too_long', {
              limit: ACTION_LIMITS.URL,
            }),
          })
          .optional(),
      ),
      method: z.nativeEnum(HttpMethodEnum),
      headers: z
        .array(
          z.object({
            id: z.number().or(z.string()),
            name: z.string().max(ACTION_LIMITS.BODY_KEY, {
              message: i18n.t('actions_page:sheet.form.validation.key_too_long', {
                limit: ACTION_LIMITS.BODY_KEY,
              }),
            }),
            value: z.string().max(ACTION_LIMITS.BODY_VALUE, {
              message: i18n.t('actions_page:sheet.form.validation.value_too_long', {
                limit: ACTION_LIMITS.BODY_VALUE,
              }),
            }),
          }),
        )
        .refine((items) => {
          const names = items.map((item) => item.name.trim());
          return new Set(names).size === names.length;
        }),
      body_items: z
        .array(
          z.object({
            id: z.number().or(z.string()),
            name: z.string().max(ACTION_LIMITS.BODY_KEY, {
              message: i18n.t('actions_page:sheet.form.validation.key_too_long', {
                limit: ACTION_LIMITS.BODY_KEY,
              }),
            }),
            content: z.string().max(ACTION_LIMITS.BODY_VALUE, {
              message: i18n.t('actions_page:sheet.form.validation.value_too_long', {
                limit: ACTION_LIMITS.BODY_VALUE,
              }),
            }),
          }),
        )
        .refine((items) => {
          const names = items.map((item) => item.name.trim());
          return new Set(names).size === names.length;
        }),
      body_items_json_enable: z.boolean(),
      body_items_json: z
        .string()
        .optional()
        .refine(
          (val) => {
            if (!val) return true;

            try {
              JSON.parse(val);
              return true;
            } catch {
              return false;
            }
          },
          {
            message: 'Invalid JSON format',
          },
        ),
      query_items: z
        .array(
          z.object({
            id: z.number().or(z.string()),
            name: z.string().max(ACTION_LIMITS.QUERY_KEY, {
              message: i18n.t('actions_page:sheet.form.validation.key_too_long', {
                limit: ACTION_LIMITS.QUERY_KEY,
              }),
            }),
            value: z.string().max(ACTION_LIMITS.QUERY_VALUE, {
              message: i18n.t('actions_page:sheet.form.validation.value_too_long', {
                limit: ACTION_LIMITS.QUERY_VALUE,
              }),
            }),
          }),
        )
        .refine((items) => {
          const names = items.map((item) => item.name.trim());
          return new Set(names).size === names.length;
        }),
      parameters: z
        .array(
          z.object({
            id: z.number().or(z.string()),
            name: z.string().max(ACTION_LIMITS.PARAMETER_KEY),
            description: z.string().max(ACTION_LIMITS.PARAMETER_DESCRIPTION),
            default_value: z
              .string()
              .max(ACTION_LIMITS.PARAMETER_DEFAULT_VALUE)
              .or(z.number())
              .or(z.boolean()),
            is_required: z.boolean(),
            type: z.nativeEnum(ParameterTypeEnum),
          }),
        )
        .refine((items) => {
          const names = items.map((item) => item.name.trim());
          return new Set(names).size === names.length;
        }),
      response_filters: z
        .array(
          z.object({
            id: z.number().or(z.string()),
            json_path: z
              .string()
              .min(1, {
                message: i18n.t('actions_page:sheet.form.validation.jsonpath_required'),
              })
              .max(ACTION_LIMITS.RESPONSE_FILTER_PATH, {
                message: i18n.t('actions_page:sheet.form.validation.jsonpath_too_long', {
                  limit: ACTION_LIMITS.RESPONSE_FILTER_PATH,
                }),
              }),
          }),
        )
        .max(ACTION_LIMITS.RESPONSE_FILTER_COUNT, {
          message: i18n.t('actions_page:sheet.form.validation.response_filter_too_many', {
            limit: ACTION_LIMITS.RESPONSE_FILTER_COUNT,
          }),
        })
        .default([]),
      response_metadata_captures: buildMetadataCaptureSchema(),
    });
  }

  return z.object({
    name: z
      .string()
      .max(ACTION_LIMITS.NAME, {
        message: i18n.t('actions_page:sheet.form.name.limit_reached', {
          limit: ACTION_LIMITS.NAME,
        }),
      })
      .min(1, { message: i18n.t('actions_page:sheet.form.validation.name_error_required') }),
    thinking_message: z.string().max(ACTION_LIMITS.THINKING_MESSAGE, {
      message: i18n.t('actions_page:sheet.form.thinking_message.limit_reached', {
        limit: ACTION_LIMITS.THINKING_MESSAGE,
      }),
    }),
    description: z
      .string()
      .max(ACTION_LIMITS.DESCRIPTION, {
        message: i18n.t('actions_page:sheet.form.validation.description_error_too_long', {
          limit: ACTION_LIMITS.DESCRIPTION,
        }),
      })
      .min(1, {
        message: i18n.t('actions_page:sheet.form.validation.description_error_required'),
      }),
    url: z
      .string()
      .min(1, { message: i18n.t('actions_page:sheet.form.validation.action_url_error') })
      .max(ACTION_LIMITS.URL, {
        message: i18n.t('actions_page:sheet.form.validation.action_url_error_too_long', {
          limit: ACTION_LIMITS.URL,
        }),
      }),
    method: z.nativeEnum(HttpMethodEnum),
    headers: z
      .array(
        z.object({
          id: z.number().or(z.string()),
          name: z
            .string()
            .min(1, { message: i18n.t('actions_page:sheet.form.validation.key_required') })
            .max(ACTION_LIMITS.HEADER_KEY, {
              message: i18n.t('actions_page:sheet.form.validation.key_too_long', {
                limit: ACTION_LIMITS.HEADER_KEY,
              }),
            }),
          value: z
            .string()
            .min(1, { message: i18n.t('actions_page:sheet.form.validation.value_required') })
            .max(ACTION_LIMITS.HEADER_VALUE, {
              message: i18n.t('actions_page:sheet.form.validation.value_too_long', {
                limit: ACTION_LIMITS.HEADER_VALUE,
              }),
            }),
        }),
      )
      .refine((items) => {
        const names = items.map((item) => item.name.trim());
        return new Set(names).size === names.length;
      }),
    body_items: z
      .array(
        z.object({
          id: z.number().or(z.string()),
          name: z
            .string()
            .min(1, { message: i18n.t('actions_page:sheet.form.validation.key_required') })
            .max(ACTION_LIMITS.BODY_KEY, {
              message: i18n.t('actions_page:sheet.form.validation.key_too_long', {
                limit: ACTION_LIMITS.BODY_KEY,
              }),
            }),
          content: z
            .string()
            .min(1, { message: i18n.t('actions_page:sheet.form.validation.value_required') })
            .max(ACTION_LIMITS.BODY_VALUE, {
              message: i18n.t('actions_page:sheet.form.validation.value_too_long', {
                limit: ACTION_LIMITS.BODY_VALUE,
              }),
            }),
        }),
      )
      .refine((items) => {
        const names = items.map((item) => item.name.trim());
        return new Set(names).size === names.length;
      }),
    body_items_json_enable: z.boolean(),
    body_items_json: z
      .string()
      .optional()
      .refine(
        (val) => {
          if (!val) return true;

          try {
            JSON.parse(val);
            return true;
          } catch {
            return false;
          }
        },
        {
          message: i18n.t('actions_page:sheet.form.validation.invalid_json'),
        },
      ),
    query_items: z
      .array(
        z.object({
          id: z.number().or(z.string()),
          name: z
            .string()
            .min(1, { message: i18n.t('actions_page:sheet.form.validation.key_required') })
            .max(ACTION_LIMITS.QUERY_KEY, {
              message: i18n.t('actions_page:sheet.form.validation.key_too_long', {
                limit: ACTION_LIMITS.QUERY_KEY,
              }),
            }),
          value: z
            .string()
            .min(1, { message: i18n.t('actions_page:sheet.form.validation.value_required') })
            .max(ACTION_LIMITS.QUERY_VALUE, {
              message: i18n.t('actions_page:sheet.form.validation.value_too_long', {
                limit: ACTION_LIMITS.QUERY_VALUE,
              }),
            }),
        }),
      )
      .refine((items) => {
        const names = items.map((item) => item.name.trim());
        return new Set(names).size === names.length;
      }),
    parameters: z
      .array(
        z.object({
          id: z.number().or(z.string()),
          name: z.string().min(1).max(ACTION_LIMITS.PARAMETER_KEY),
          description: z.string().min(1).max(ACTION_LIMITS.PARAMETER_DESCRIPTION),
          default_value: z
            .string()
            .max(ACTION_LIMITS.PARAMETER_DEFAULT_VALUE)
            .or(z.number())
            .or(z.boolean()),
          is_required: z.boolean(),
          type: z.nativeEnum(ParameterTypeEnum),
        }),
      )
      .refine((items) => {
        const names = items.map((item) => item.name.trim());
        return new Set(names).size === names.length;
      }),
    response_filters: z
      .array(
        z.object({
          id: z.number().or(z.string()),
          json_path: z
            .string()
            .min(1, {
              message: i18n.t('actions_page:sheet.form.validation.jsonpath_required'),
            })
            .max(ACTION_LIMITS.RESPONSE_FILTER_PATH, {
              message: i18n.t('actions_page:sheet.form.validation.jsonpath_too_long', {
                limit: ACTION_LIMITS.RESPONSE_FILTER_PATH,
              }),
            }),
        }),
      )
      .max(ACTION_LIMITS.RESPONSE_FILTER_COUNT, {
        message: i18n.t('actions_page:sheet.form.validation.response_filter_too_many', {
          limit: ACTION_LIMITS.RESPONSE_FILTER_COUNT,
        }),
      })
      .default([]),
    response_metadata_captures: buildMetadataCaptureSchema(),
  });
};

export type HttpActionForm = z.infer<ReturnType<typeof actionSchema>>;

export type ValidationCheckError = {
  are_all_parameters_assigned: boolean;
  are_external_badges_valid: boolean;
  are_parameter_names_not_empty: boolean;
  are_parameter_names_unique: boolean;
  are_parameters_not_more_than_max: boolean;
  are_static_body_items_names_unique: boolean;
  are_static_header_names_unique: boolean;
  are_static_query_names_unique: boolean;
  has_url: boolean;
  is_description_not_empty: boolean;
  is_name_not_empty: boolean;
  single_body_source: boolean;
};

const VALIDATION_CHECK_RULES: Array<[keyof ValidationCheckError, string]> = [
  ['has_url', 'actions_page:sheet.form.validation.url_required'],
  ['are_all_parameters_assigned', 'actions_page:sheet.form.validation.all_parameters_assigned'],
  ['are_static_header_names_unique', 'actions_page:sheet.form.validation.header_names_unique'],
  [
    'are_static_body_items_names_unique',
    'actions_page:sheet.form.validation.body_items_names_unique',
  ],
  ['are_static_query_names_unique', 'actions_page:sheet.form.validation.query_items_names_unique'],
  ['single_body_source', 'actions_page:sheet.form.validation.single_body_source'],
  ['are_external_badges_valid', 'actions_page:sheet.form.validation.external_badges_invalid'],
];

export const getValidationCheckMessage = (
  check: ValidationCheckError,
  t: (key: string) => string,
): string | null => {
  const failedRule = VALIDATION_CHECK_RULES.find(([field]) => !check[field]);
  return failedRule ? t(failedRule[1]) : null;
};

/**
 * Strip the `Value error, ` / `Type error, ` prefix Pydantic prepends to user-raised
 * exceptions so the message reads naturally in the UI.
 */
const cleanPydanticPrefix = (message: string): string =>
  message.replace(/^(Value error|Type error|Assertion error),\s*/i, '');

export type ResponseFilterRowError = { rowIndex: number; message: string };

/**
 * Pull every per-row response_filter error out of a 422 Pydantic-validation
 * response. Ninja keys errors by field path (e.g.
 * `body.data.response_filters.0.json_path`); we collect all of them so the
 * caller can map each one back to its row and render an inline error there
 * instead of forcing the merchant to read a single cryptic toast.
 */
export const getResponseFilterRowErrors = (data: unknown): ResponseFilterRowError[] => {
  const errors = (data as { errors?: Record<string, Array<{ message?: string }>> } | undefined)
    ?.errors;
  if (!errors) return [];
  const rows: ResponseFilterRowError[] = [];
  for (const [path, items] of Object.entries(errors)) {
    const match = path.match(/response_filters\.(\d+)\.json_path/);
    if (!match) continue;
    const message = items?.[0]?.message;
    if (!message) continue;
    rows.push({ rowIndex: Number(match[1]), message: cleanPydanticPrefix(message) });
  }
  return rows;
};

export type MetadataCaptureRowError = {
  rowIndex: number;
  field: 'json_path' | 'metadata_key';
  message: string;
};

/**
 * Pull every per-row response_metadata_capture error (json_path or metadata_key)
 * out of a 422 Pydantic-validation response so each maps back to its row.
 */
export const getMetadataCaptureRowErrors = (data: unknown): MetadataCaptureRowError[] => {
  const errors = (data as { errors?: Record<string, Array<{ message?: string }>> } | undefined)
    ?.errors;
  if (!errors) return [];
  const rows: MetadataCaptureRowError[] = [];
  for (const [path, items] of Object.entries(errors)) {
    const match = path.match(/response_metadata_captures\.(\d+)\.(json_path|metadata_key)/);
    if (!match) continue;
    const message = items?.[0]?.message;
    if (!message) continue;
    rows.push({
      rowIndex: Number(match[1]),
      field: match[2] as 'json_path' | 'metadata_key',
      message: cleanPydanticPrefix(message),
    });
  }
  return rows;
};

/**
 * Fallback for non-row 422 errors (anything not under `response_filters.N.json_path`
 * or `response_metadata_captures.N.<field>`).
 */
export const getFirstFieldValidationError = (data: unknown): string | null => {
  const errors = (data as { errors?: Record<string, Array<{ message?: string }>> } | undefined)
    ?.errors;
  if (!errors) return null;
  for (const [path, items] of Object.entries(errors)) {
    if (path.match(/response_filters\.\d+\.json_path/)) continue;
    if (path.match(/response_metadata_captures\.\d+\.(json_path|metadata_key)/)) continue;
    const message = items?.[0]?.message;
    if (message) return cleanPydanticPrefix(message);
  }
  return null;
};

export const useActionForm = (
  httpRequestAction?: HttpActionDto,
  hubspotTemplate?: HubSpotTemplate,
) => {
  const { t } = useTranslation('actions_page');
  const queryClient = useQueryClient();
  const { open, name, closeModal } = useModalStore();
  const { capture } = usePostHogAnalytics();

  useEffect(() => {
    if (!httpRequestAction) return;
  }, [httpRequestAction]);

  const form = useForm<HttpActionForm>({
    mode: 'onSubmit',
    shouldFocusError: false,
    defaultValues: {
      name: '',
      description: '',
      url: '',
      method: HttpMethodEnum.GET,
      headers: [],
      body_items: [],
      body_items_json_enable: false,
      body_items_json: JSON.stringify({}, null, 2),
      query_items: [],
      parameters: [],
      response_filters: [],
      response_metadata_captures: [],
    },
    resolver: zodResolver(actionSchema(!!httpRequestAction?.is_active)),
  });

  useEffect(() => {
    if (open && name == MODAL_NAMES.HTTP_REQUEST_ACTION_SHEET && httpRequestAction) {
      const headers = httpRequestAction?.headers.length
        ? HttpActionHelper.replaceParameterIdsWithNames(
            httpRequestAction.headers,
            httpRequestAction.parameters,
          )
        : [];

      const body = httpRequestAction?.body_items.length
        ? HttpActionHelper.replaceParameterIdsWithNames(
            httpRequestAction.body_items,
            httpRequestAction.parameters,
          )
        : [];

      const query = httpRequestAction?.query_items.length
        ? HttpActionHelper.replaceParameterIdsWithNames(
            httpRequestAction.query_items,
            httpRequestAction.parameters,
          )
        : [];

      const url = HttpActionHelper.replaceParameterIdsWithNamesForUrl(
        httpRequestAction?.url || '',
        httpRequestAction?.parameters,
      );

      const bodyItemsJson = httpRequestAction?.body_json
        ? HttpActionHelper.replaceParameterIdsWithNamesInString(
            JSON.stringify(httpRequestAction.body_json, null, 2),
            httpRequestAction?.parameters,
          )
        : JSON.stringify({}, null, 2);

      const parameters =
        httpRequestAction?.parameters.map((param) => {
          if (param.type === ParameterTypeEnum.Boolean) {
            return { ...param, default_value: String(Number(param.default_value)) };
          }
          return { ...param, default_value: param.default_value || '' };
        }) || [];

      if (hubspotTemplate) {
        form.reset({
          name: t(hubspotTemplate.nameKey),
          description: hubspotTemplate.aiDescription,
          url: hubspotTemplate.url,
          method: hubspotTemplate.method,
          headers: hubspotTemplate.headers.map((h) => ({
            id: crypto.randomUUID(),
            name: h.name,
            value: h.value,
          })),
          body_items: [],
          body_items_json_enable: hubspotTemplate.bodyJson !== JSON.stringify({}, null, 2),
          body_items_json: hubspotTemplate.bodyJson,
          query_items: hubspotTemplate.queryItems.map((q) => ({
            id: crypto.randomUUID(),
            name: q.name,
            value: q.value,
          })),
          parameters: hubspotTemplate.parameters.map((p) => ({
            id: crypto.randomUUID(),
            name: p.name,
            description: p.description,
            type: p.type,
            default_value: p.default_value,
            is_required: p.is_required,
          })),
          thinking_message: hubspotTemplate.thinkingMessage,
          response_filters: [],
          response_metadata_captures: [],
        });
      } else {
        form.reset({
          name: httpRequestAction?.name || '',
          description: httpRequestAction?.description || '',
          url: url,
          body_items_json_enable: !!httpRequestAction?.body_json,
          body_items_json: bodyItemsJson,
          method: httpRequestAction?.method || HttpMethodEnum.GET,
          headers: headers,
          body_items: body,
          query_items: query,
          parameters: parameters,
          thinking_message: httpRequestAction?.thinking_message || '',
          response_filters: httpRequestAction?.response_filters ?? [],
          response_metadata_captures: httpRequestAction?.response_metadata_captures ?? [],
        });
      }
    }
  }, [open, name, httpRequestAction, hubspotTemplate]);

  const onSubmit = async (values: HttpActionForm) => {
    if (!httpRequestAction) return;
    const { body_items_json_enable, body_items_json, ...formValues } = values;

    const httpActionPayload: Omit<HttpActionForm, 'body_items_json_enable' | 'body_items_json'> &
      Record<'body_json', any> = {
      ...formValues,
      url: HttpActionHelper.injectParameterIdsForUrl(values.url || '', values.parameters),
      headers: HttpActionHelper.injectParameterIds(
        values.headers,
        values.parameters,
      ) as HttpActionForm['headers'],
      body_items: body_items_json_enable
        ? []
        : (HttpActionHelper.injectParameterIds(
            values.body_items,
            values.parameters,
          ) as HttpActionForm['body_items']),
      query_items: HttpActionHelper.injectParameterIds(
        values.query_items,
        values.parameters,
      ) as HttpActionForm['query_items'],
      body_json: body_items_json_enable
        ? JSON.parse(
            HttpActionHelper.injectParameterIdsInString(
              body_items_json || '',
              formValues.parameters,
            ) || JSON.stringify({}, null, 2),
          )
        : null,
      parameters: values.parameters.map((param) => ({
        ...param,
        default_value: ActionHelper.mapParameterTypeToValueType(param.default_value, param.type),
      })),
    };

    try {
      const responseData = await ActionsApi.updateHttp(httpRequestAction!.id, httpActionPayload);

      capture(PostHogEventEnum.ActionUpdated, {
        action_id: httpRequestAction!.id,
        action_type: 'http_request',
        has_parameters: values.parameters.length > 0,
        parameter_count: values.parameters.length,
      });

      void queryClient.invalidateQueries({ queryKey: [QueryKey.actions.basic.list] });
      void queryClient.setQueryData<HttpActionDto>(
        [QueryKey.actions.httpRequest.get, httpRequestAction!.id],
        responseData,
      );

      toast.success(t('toast.action_updated'));
      closeModal();
    } catch (error) {
      if (error instanceof AxiosError) {
        if (error.status === HttpStatusCode.BadRequest) {
          const firstError = (error.response?.data as ErrorResponse | undefined)?.errors?.root?.[0];

          const validationChecks = firstError?.options?.validation_checks;
          if (validationChecks) {
            const message = getValidationCheckMessage(validationChecks, t);
            if (message) {
              toast.error(message);
              throw error;
            }
          }

          const serverMessage = firstError?.message;
          if (serverMessage) {
            toast.error(t('toast.update_failed'), { description: serverMessage });
            throw error;
          }
        }

        if (error.status === HttpStatusCode.UnprocessableEntity) {
          const responseFilterRowErrors = getResponseFilterRowErrors(error.response?.data);
          if (responseFilterRowErrors.length > 0) {
            for (const { rowIndex, message } of responseFilterRowErrors) {
              form.setError(`response_filters.${rowIndex}.json_path` as const, {
                type: 'server',
                message,
              });
            }
            toast.error(t('toast.update_failed'), {
              description: t('sheet.form.validation.response_filter_rows_invalid'),
            });
            throw error;
          }
          const metadataCaptureRowErrors = getMetadataCaptureRowErrors(error.response?.data);
          if (metadataCaptureRowErrors.length > 0) {
            for (const { rowIndex, field, message } of metadataCaptureRowErrors) {
              form.setError(`response_metadata_captures.${rowIndex}.${field}` as const, {
                type: 'server',
                message,
              });
            }
            toast.error(t('toast.update_failed'), {
              description: t('sheet.form.validation.memory_capture_rows_invalid'),
            });
            throw error;
          }
          const firstFieldError = getFirstFieldValidationError(error.response?.data);
          if (firstFieldError) {
            toast.error(t('toast.update_failed'), { description: firstFieldError });
            throw error;
          }
        }

        toast.error(t('toast.update_failed'));
      }

      console.error(error);
      throw error;
    }
  };

  const onInvalid = (errors: Record<string, unknown>) => {
    console.error('Validation errors:', errors);
    if (httpRequestAction?.is_valid) toast.error(t('toast.invalid_update_on_is_active'));
  };

  return {
    form,
    onSubmit,
    onInvalid,
  };
};
